import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { MagnifyingGlass, X } from "@phosphor-icons/react";

// Búsqueda global de expedientes. Aparece en la topbar (siempre visible)
// y permite saltar al expediente con un click. Busca por:
// nº siniestro (compañía o gabinete), póliza, asegurado, NIF, dirección,
// código postal, municipio, referencia catastral, nº dobilas.
export default function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Cerrar al click fuera
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Atajo "/" para enfocar
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && !["INPUT", "TEXTAREA", "SELECT"].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Búsqueda con debounce
  useEffect(() => {
    if (!q || q.trim().length < 2) {
      setItems([]);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      api
        .get("/expedientes/_search", { params: { q: q.trim(), limit: 10 } })
        .then((r) => {
          setItems(r.data || []);
          setActiveIdx(0);
        })
        .catch(() => setItems([]))
        .finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  const go = (id) => {
    setOpen(false);
    setQ("");
    setItems([]);
    navigate(`/review/${id}`);
  };

  const onKeyDown = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(items.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const t = items[activeIdx];
      if (t) go(t.id);
    } else if (e.key === "Escape") {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div ref={wrapRef} className="relative flex-1 max-w-xl" data-testid="global-search">
      <div className="relative">
        <MagnifyingGlass
          className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none z-10"
          weight="regular"
        />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => q && setOpen(true)}
          onKeyDown={onKeyDown}
          data-testid="global-search-input"
          placeholder="Buscar por nº siniestro, póliza, teléfono, apellidos, dirección..."
          className="w-full bg-background border border-border rounded-lg !pl-10 !pr-9 text-[13px] text-foreground placeholder:text-[#9CA3AF] focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-colors"
          style={{ height: "34px" }}
        />
        {q && (
          <button
            onClick={() => { setQ(""); setItems([]); inputRef.current?.focus(); }}
            data-testid="global-search-clear"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted hover:text-foreground"
            title="Limpiar"
          >
            <X className="w-3 h-3" weight="bold" />
          </button>
        )}
      </div>

      {open && q.trim().length >= 2 && (
        <div
          className="absolute z-50 mt-1 left-0 right-0 bg-white border border-border rounded-md shadow-xl overflow-hidden"
          data-testid="global-search-results"
        >
          {loading ? (
            <div className="px-4 py-3 text-sm text-muted">Buscando...</div>
          ) : items.length === 0 ? (
            <div className="px-4 py-3 text-sm text-muted" data-testid="global-search-empty">
              No se ha encontrado nada para los resultados de búsqueda.
            </div>
          ) : (
            <ul className="max-h-[60vh] overflow-y-auto">
              {items.map((it, i) => {
                const e = it.extracted_data || {};
                const ns = e.numero_siniestro;
                const nsg = e.numero_siniestro_gabinete;
                const subtitle = [
                  e.asegurado_nombre,
                  e.direccion_riesgo,
                  e.municipio || e.provincia,
                ].filter(Boolean).join(" · ");
                return (
                  <li
                    key={it.id}
                    onMouseEnter={() => setActiveIdx(i)}
                    onClick={() => go(it.id)}
                    data-testid={`global-search-item-${it.id}`}
                    className={`px-4 py-2.5 cursor-pointer border-b border-border/60 last:border-b-0 ${
                      i === activeIdx ? "bg-brand/5" : "hover:bg-surface"
                    }`}
                  >
                    <div className="flex items-baseline justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-[13px] truncate">
                          {ns ? <span className="font-mono">{ns}</span> : <span className="text-muted italic">sin nº</span>}
                          {nsg && (
                            <span className="ml-2 text-[10px] font-mono font-bold bg-brand/10 text-brand px-1.5 py-0.5 rounded">
                              GAB · {nsg}
                            </span>
                          )}
                        </div>
                        {subtitle && (
                          <div className="text-[11px] text-muted truncate mt-0.5">{subtitle}</div>
                        )}
                      </div>
                      <div className="text-[10px] text-muted whitespace-nowrap font-mono">
                        {it.detected_aseguradora || e.aseguradora || "—"}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
